import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, StatusPill } from "@/components/ui-kit";
import { formatINR, formatNum, formatDate } from "@/lib/format";
import {
  Calendar, Activity, Clock, Car, Wrench, Users, IndianRupee, AlertCircle,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line } from "recharts";

export default function OwnerDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/owner/stats").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div data-testid="owner-dashboard-page">
      <PageHeader title="Operations Control Room" subtitle="Today's pulse across your fleet — bookings, vehicles, drivers, money." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard testid="stat-todays-trips" label="Today's Trips" value={formatNum(data.todays_trips)} icon={Calendar} />
        <StatCard testid="stat-running-trips" label="Running" value={formatNum(data.running_trips)} icon={Activity} accent />
        <StatCard testid="stat-upcoming" label="Upcoming" value={formatNum(data.upcoming_trips)} icon={Clock} />
        <StatCard testid="stat-monthly-revenue" label="Monthly Revenue" value={formatINR(data.monthly_revenue)} icon={IndianRupee} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-available-vehicles" label="Available Vehicles" value={`${data.available_vehicles}/${data.total_vehicles}`} icon={Car} />
        <StatCard testid="stat-maintenance" label="Maintenance" value={formatNum(data.maintenance_vehicles)} icon={Wrench} />
        <StatCard testid="stat-active-drivers" label="Active Drivers" value={`${data.active_drivers}/${data.total_drivers}`} icon={Users} />
        <StatCard testid="stat-outstanding" label="Outstanding" value={formatINR(data.outstanding_payments)} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Revenue vs Expense vs Profit</div>
          <div className="font-display font-bold text-xl text-asphalt mt-1 mb-4">Last 6 months</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.monthly_chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
              <YAxis stroke="#71717a" fontSize={12} />
              <Tooltip formatter={(v) => formatINR(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#FFB800" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" name="Expense" fill="#09090B" radius={[4, 4, 0, 0]} />
              <Bar dataKey="profit" name="Profit" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Document Expiry</div>
          </div>
          <div className="font-display font-bold text-xl text-asphalt mb-4">Next 30 days</div>
          {data.expiring_docs.length === 0 ? (
            <div className="text-sm text-zinc-500">All documents up to date. ✓</div>
          ) : (
            <ul className="space-y-2">
              {data.expiring_docs.map((d, i) => (
                <li key={i} className="flex items-center justify-between p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                  <div>
                    <div className="font-medium text-asphalt">{d.ref}</div>
                    <div className="text-zinc-500">{d.doc.replace("_expiry", "").toUpperCase()}</div>
                  </div>
                  <div className="text-amber-700 font-mono">{formatDate(d.expiry)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="font-display font-bold text-asphalt">Recent Bookings</div>
        </div>
        {data.recent_bookings.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No bookings yet — create your first booking.</div>
        ) : (
          <table className="tbl">
            <thead><tr><th>#</th><th>Client</th><th>Type</th><th>Pickup → Drop</th><th>Fare</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {data.recent_bookings.map((b) => (
                <tr key={b.id}>
                  <td className="font-mono text-xs">{b.booking_number}</td>
                  <td className="font-medium text-asphalt">{b.client_name}</td>
                  <td>{b.booking_type}</td>
                  <td>{b.pickup} → {b.drop || "—"}</td>
                  <td className="font-mono">{formatINR(b.fare)}</td>
                  <td><StatusPill status={b.status} /></td>
                  <td className="text-zinc-500">{formatDate(b.journey_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
